import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Lock, FileWarning, ShieldAlert, Loader2, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  usePublicTransferSummary,
  usePublicTransferManifest,
  usePublicTransferActions,
  downloadTransferFile,
  TRANSFER_UNAVAILABLE_MESSAGE,
} from '../hooks';
import { isApiErrorCode } from '@/lib/api-error-code';
import { TransferDocumentViewer } from '../components/TransferDocumentViewer';
import { TransferQuestionsPanel } from '../components/TransferQuestionsPanel';

/**
 * The recipient-facing download page. NO AUTH — the share token in the URL is
 * the only credential.
 *
 * ---------------------------------------------------------------------------
 * COPY RULES — these are security controls wearing UI clothes
 *
 * 1. **Never say why a link is unavailable.** Unknown, revoked, expired,
 *    exhausted, unpaid, and deleted all come back as the same 404 on purpose;
 *    distinguishing them would turn the page into an existence oracle for
 *    anyone probing tokens. One message, always.
 *
 * 2. **Never claim view-only prevents anything.** A browser must receive
 *    renderable content, so a determined viewer can always capture it. The
 *    honest promise is deterrence and attribution.
 *
 * 3. **The file list is not rendered until every gate passes.** Filenames and
 *    folder paths are themselves confidential — `/Project Cobalt/Term Sheet —
 *    Acquirer B.pdf` discloses a deal before a byte moves. The server enforces
 *    this; the client must not fetch the manifest early and cache it either.
 *
 * 4. **`over_capacity` is the one unavailability the visitor CAN be told
 *    about.** Every other cause collapses to one word, but a file beyond the
 *    link's covered capacity is real, readable, and fixable by the sender —
 *    saying so lets the visitor ask for it instead of assuming it was
 *    withdrawn. It discloses nothing about the file itself.
 * ---------------------------------------------------------------------------
 */
export function PublicDownloadPage() {
  const { token } = useParams<{ token: string }>();

  /**
   * Suppress the `Referer` header for as long as this page is mounted.
   *
   * The share token is in this page's URL — it has to be, for an emailed link
   * to work — which makes the URL itself a bearer credential. Any outbound
   * navigation or subresource request would otherwise carry it to a third-party
   * origin in `Referer`.
   *
   * Scoped to this route rather than set globally in `index.html`: the rest of
   * the app has no credential in its URLs, and a blanket `no-referrer` would
   * silently change analytics and OAuth behaviour everywhere for a problem that
   * exists only here. Removed on unmount for the same reason.
   *
   * This is the client half of the same control the API applies via
   * `Referrer-Policy` / `X-Robots-Tag` on `/v1/public/fias-transfer` — headers
   * on an XHR response cannot govern the document that issued it.
   */
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
  const { summary, loading, unavailable } = usePublicTransferSummary(token);
  const actions = usePublicTransferActions(token);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [openingPreviewId, setOpeningPreviewId] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  /**
   * Documents GuardDuty could not scan. Learned at download time — the manifest
   * deliberately does not read scan verdicts, so this is populated as files are
   * actually taken, which is also when the information matters.
   */
  const [notScannableIds, setNotScannableIds] = useState<Set<string>>(new Set());

  const gatesPassed = !!summary && summary.unlocked.password && summary.unlocked.nda;
  const { manifest, loading: manifestLoading } = usePublicTransferManifest(token, gatesPassed);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Skeleton className="mb-4 h-8 w-2/3" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  // One message for every inaccessible state — see copy rule 1.
  if (unavailable || !summary) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-center">
        <FileWarning className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="mb-2 text-xl font-semibold">{TRANSFER_UNAVAILABLE_MESSAGE}</h1>
        <p className="text-sm text-muted-foreground">
          The link may have expired or been turned off by the person who sent it.
        </p>
      </div>
    );
  }

  const handleUnlock = async () => {
    setPasswordError(null);
    try {
      await actions.unlock.mutateAsync(password);
      setPassword('');
    } catch {
      // Generic on purpose: a specific message here would let an attacker
      // distinguish "wrong password" from other failures.
      setPasswordError('Incorrect password.');
    }
  };

  /**
   * Open the viewer.
   *
   * The preview endpoint requires an open download window exactly as the byte
   * endpoints do — reading IS the delivery on a view-only link, so it consumes
   * an authorization like any other payload pass.
   *
   * This must therefore open the window itself. On a view-only link there is NO
   * Download button, so the download path that used to be the only thing
   * opening a window is unreachable: the viewer would mount, fetch, get
   * DOWNLOAD_WINDOW_REQUIRED, and report "this file cannot be previewed" —
   * making the flagship data-room capability unusable through the UI.
   *
   * Opening BEFORE mounting, not inside the viewer, so a failure here surfaces
   * on the file row (where the user can retry) rather than as an unexplained
   * empty viewer.
   */
  const handlePreview = async (documentId: string) => {
    setPreviewError(null);
    setOpeningPreviewId(documentId);
    try {
      // Idempotent server-side while a window is already open, so re-opening a
      // second file does not burn another authorization.
      if (!manifest?.windowExpiresAt || new Date(manifest.windowExpiresAt) <= new Date()) {
        await actions.openWindow.mutateAsync();
      }
      setPreviewingId(documentId);
    } catch {
      setPreviewError('This file could not be opened. The link may have no views remaining.');
    } finally {
      setOpeningPreviewId(null);
    }
  };

  const handleDownload = async (documentId: string) => {
    if (!token) return;
    setDownloadingId(documentId);
    setDownloadError(null);
    try {
      // Opening the window consumes one of the sender's authorizations. The
      // server is idempotent while a window is already open, so re-clicking
      // does not burn a second one.
      if (!manifest?.windowExpiresAt || new Date(manifest.windowExpiresAt) <= new Date()) {
        await actions.openWindow.mutateAsync();
      }
      const { notScannable } = await downloadTransferFile(token, documentId);
      if (notScannable) {
        setNotScannableIds((previous) => new Set(previous).add(documentId));
      }
    } catch (err) {
      // Without this the whole failure was invisible: the click rejected, the
      // spinner cleared, and the page looked untouched. Every refusal below is
      // reachable in normal use — the malware gate holds a file back, a window
      // lapses mid-session, the sender's authorizations run out — so silence
      // here reads as a broken button on the arche's one recipient-facing
      // action.
      //
      // Copy rule 1 governs whether the LINK exists and is deliberately not
      // relaxed. This visitor already passed every gate and is holding a list
      // of filenames, so naming a per-file outcome discloses nothing further —
      // these mirror the server's own messages rather than sharpening them.
      if (isApiErrorCode(err, 'FILE_PROCESSING')) {
        setDownloadError('This file is still being processed. Try again shortly.');
      } else if (isApiErrorCode(err, 'FILE_UNAVAILABLE')) {
        setDownloadError('This file is not available.');
      } else if (isApiErrorCode(err, 'DOWNLOAD_NOT_PERMITTED')) {
        setDownloadError('This link is view-only.');
      } else if (isApiErrorCode(err, 'DOWNLOAD_WINDOW_REQUIRED')) {
        setDownloadError('This download window has closed. Try the download again.');
      } else {
        setDownloadError('That download could not be started. Try again shortly.');
      }
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-semibold">{summary.title}</h1>
        {summary.message && <p className="mt-1 text-muted-foreground">{summary.message}</p>}
        <p className="mt-2 text-sm text-muted-foreground">
          {summary.fileCount} {summary.fileCount === 1 ? 'file' : 'files'} ·{' '}
          {formatBytes(summary.totalBytes)} · expires{' '}
          {new Date(summary.expiresAt).toLocaleDateString()}
        </p>
      </header>

      {!summary.unlocked.password && (
        <section className="rounded-lg border p-4">
          <div className="mb-3 flex items-center gap-2">
            <Lock className="h-4 w-4" />
            <h2 className="font-medium">This link is password protected</h2>
          </div>
          <div className="flex gap-2">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              maxLength={128}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleUnlock();
              }}
            />
            <Button
              onClick={() => void handleUnlock()}
              disabled={!password || actions.unlock.isPending}
            >
              {actions.unlock.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlock'}
            </Button>
          </div>
          {passwordError && <p className="mt-2 text-sm text-destructive">{passwordError}</p>}
        </section>
      )}

      {summary.unlocked.password && !summary.unlocked.nda && (
        <section className="rounded-lg border p-4">
          <div className="mb-3 flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            <h2 className="font-medium">Agreement required</h2>
          </div>
          <p className="mb-3 text-sm text-muted-foreground">
            The sender asks you to agree to keep these files confidential before viewing them.
          </p>
          <Button
            onClick={() => void actions.acceptNda.mutateAsync()}
            disabled={actions.acceptNda.isPending}
          >
            I agree
          </Button>
        </section>
      )}

      {gatesPassed && (
        <section>
          <h2 className="mb-3 font-medium">Files</h2>
          {manifestLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <ul className="space-y-2">
              {(manifest?.files ?? []).map((file) => (
                <li
                  key={file.documentId}
                  className="flex items-center justify-between rounded-lg border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{file.name ?? 'File'}</p>
                    <p className="text-xs text-muted-foreground">
                      {file.available
                        ? formatBytes(file.sizeBytes ?? 0)
                        : file.unavailableReason === 'over_capacity'
                          ? // See copy rule 4 — the one cause worth naming.
                            'Beyond this link’s capacity — ask the sender for a new link'
                          : 'Unavailable'}
                      {notScannableIds.has(file.documentId) && ' · not virus-scanned'}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {/* `canPreview` is per-LINK; `previewable` is per-FILE.
                        Both have to hold — the renderer takes PDFs only, so a
                        View button on anything else is a button whose one
                        outcome is "this file cannot be previewed". Older
                        servers omit the field; treating undefined as
                        previewable keeps their behaviour unchanged rather than
                        hiding a control that does work. */}
                    {manifest?.canPreview && file.previewable !== false && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!file.available || openingPreviewId === file.documentId}
                        onClick={() => void handlePreview(file.documentId)}
                      >
                        {openingPreviewId === file.documentId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="mr-2 h-4 w-4" />
                            View
                          </>
                        )}
                      </Button>
                    )}
                    {manifest?.canDownload && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!file.available || downloadingId === file.documentId}
                        onClick={() => void handleDownload(file.documentId)}
                      >
                        {downloadingId === file.documentId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="mr-2 h-4 w-4" />
                            Download
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {manifest && manifest.canDownload && manifest.downloadAuthorizationsRemaining <= 0 && (
            <p className="mt-3 text-sm text-muted-foreground">
              This link has no downloads remaining.
            </p>
          )}
          {/* Inside the file section, next to the button that failed — a
              message under the questions panel at the bottom of the page would
              be off-screen on any transfer with more than a few files. */}
          {downloadError && <p className="mt-3 text-sm text-destructive">{downloadError}</p>}
        </section>
      )}

      {previewError && <p className="text-sm text-destructive">{previewError}</p>}

      {gatesPassed && manifest?.questionsEnabled && token && (
        <TransferQuestionsPanel token={token} />
      )}

      {previewingId && token && (
        <TransferDocumentViewer
          token={token}
          documentId={previewingId}
          onClose={() => setPreviewingId(null)}
        />
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[unit]}`;
}
